import React, { useRef, useEffect } from 'react';
import { ActiveRetailer, OnStoreHover, RetailerEntity } from '../../store/types';
import RetailerOpeningHours from '../openingHours/RetailerOpeningHours';
import { GlobalIcon, IconImage } from 'src/modules/global';
// import { classes } from 'src/utils/styles';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { useLocale, OutputChannels } from 'src/modules/locale';
import styles from './RetailerCard.module.scss';

interface RetailerCardProps {
  data: RetailerEntity;
  onRetailerHover: OnStoreHover;
  activeRetailer: ActiveRetailer;
  className?: string;
}

const RetailerCard: React.FunctionComponent<RetailerCardProps> = (props: RetailerCardProps) => {
  const KM_TO_MILES = 0.621371;

  const { getLocalizedObject } = useLocale();

  // using getLocalizedObject only for these properties. Calling it on
  // props.data is too CPU heavy because of the number of stores and it breaks
  // the app...
  const { notification } = getLocalizedObject({
    notification_de: props.data.notification_de,
    notification_en: props.data.notification_en,
  });

  const {
    className,
    data: {
      id,
      name,
      street,
      zip,
      city,
      email,
      phone,
      link,
      opening_hours: openingHours,
      lat,
      lng,
      distance,
    },
    activeRetailer: { id: activeRetailerId, scroll },
    onRetailerHover,
  } = props;
  const { outputChannel } = useLocale();
  const intl = useIntl();
  const ref = useRef() as React.MutableRefObject<HTMLInputElement>;

  // If the store is active, scroll to it to display it at the top of the Store List. Run only when activeRetailer changes
  useEffect(() => {
    if (activeRetailerId === id && ref && scroll) {
      ref.current.parentElement.classList.add('active');
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      console.log(ref.current.scrollTop);
      console.log(scroll);
    } else {
      ref.current.parentElement.classList.remove('active');
    }
  }, [activeRetailerId, id, scroll]);

  return (
    <div
      key={id}
      ref={ref}
      className={classnames(styles.card, className)}
      onMouseLeave={onRetailerHover.bind(null, 0, false)}
      onMouseEnter={onRetailerHover.bind(null, id, false)}>
      <div className={styles.hoverBackground} />
      <div className={styles.title}>{name}</div>
      <div className={styles.address}>
        {street}
        {zip && `, ${zip}`}
        {city && ` ${city}`}
      </div>
      <RetailerOpeningHours openingHours={openingHours} />
      {notification && (
        <div className={styles.notification} dangerouslySetInnerHTML={{ __html: notification }} />
      )}
      <div className={styles.details}>
        {phone && <div>{phone}</div>}
        {email && (
          <div>
            <a href={'mailto:' + email}>{email}</a>
          </div>
        )}
        <div className={styles.links}>
          <a
            href={link?.startsWith('http') ? link : `http://${link}`}
            target="_blank"
            rel="noreferrer">
            {link && intl.formatMessage({ id: 'page/store/visit_website' })}
          </a>
          <div className={styles.directions}>
            <span className={styles.distance}>
              {distance &&
                (outputChannel === OutputChannels.US
                  ? `${Math.round(distance * KM_TO_MILES)} ${intl.formatMessage({
                      id: 'page/store/miles',
                    })}`
                  : `${distance} ${intl.formatMessage({ id: 'page/store/km' })}`)}
            </span>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
              target="_blank"
              rel="noreferrer">
              {intl.formatMessage({ id: 'page/store/get_directions' })}
              <GlobalIcon image={IconImage.Link} />
            </a>
          </div>
        </div>
      </div>
      <br />
    </div>
  );
};

export default RetailerCard;
